import { z } from 'zod';
import { defineListQuery, type Page } from './list-query.schemas';

/** What happened to a driver's phone, as the devices panel lists it (gitaumoses4/chukua#59). */
export const DRIVER_DEVICE_EVENT_KINDS = [
  'signed_in',
  'replaced',
  'signed_out',
  'revoked_by_office',
  'driver_deactivated',
  'reuse_detected',
  'wipe_requested',
  'wipe_delivered',
  'wipe_superseded',
] as const;
export type DriverDeviceEventKind = (typeof DRIVER_DEVICE_EVENT_KINDS)[number];

/** `POST /drivers/:id/devices/:deviceId/revoke` and `/wipe`: the office says why, and the reason is kept with the audit entry. */
export const driverDeviceActionSchema = z.object({ reason: z.string().trim().min(1, 'Give a reason').max(500) }).strict();
export type DriverDeviceActionInput = z.infer<typeof driverDeviceActionSchema>;

/** `GET /drivers/:id/devices`: pages the history only; the current device is always returned whole. */
export const driverDeviceHistoryQuerySchema = defineListQuery({ sortFields: ['occurredAt'], defaultSort: { sort: 'occurredAt', order: 'desc' } });
export type DriverDeviceHistoryQuery = z.infer<typeof driverDeviceHistoryQuerySchema>;

/** `pending`: the office asked and the phone has not been told yet. `delivered`: the server has answered a call with the wipe signal (the phone may still have missed it). */
export type DriverDeviceWipeState = 'none' | 'pending' | 'delivered';

export interface DriverDeviceView {
  deviceId: string;
  model: string | null;
  appVersion: string | null;
  platform: string | null;
  signedInAt: string;
  lastSeenAt: string | null;
  /** False once signed out, revoked or the driver was deactivated: the phone's tokens no longer work. */
  isActive: boolean;
  wipe: {
    state: DriverDeviceWipeState;
    requestedAt: string | null;
    requestedByName: string | null;
    reason: string | null;
    deliveredAt: string | null;
  };
}

/** The devices panel: the driver's one current phone (null if they never signed in) and the history of every phone. */
export interface DriverDevicesView {
  current: DriverDeviceView | null;
  history: Page<DriverDeviceEventView>;
}

export interface DriverDeviceEventView {
  id: string;
  deviceId: string;
  kind: DriverDeviceEventKind;
  actorName: string | null;
  reason: string | null;
  detail: Record<string, string | null>;
  occurredAt: string;
}
